import React from 'react';
import { FiTrendingDown } from 'react-icons/fi';
import * as translator from '../../../../../utils/translator';
import { CareProfileProgress } from 'data-types';
import CareProfileCard from './CareProfileCard';
import { formatWeight, WeightUnit } from '../../../../../utils/weightUnits';



interface WeightProgressCardProps {
	progress: CareProfileProgress;
	language: string;
	bare?: boolean;
	weightUnit?: WeightUnit;
}

const progressGradient = 'linear-gradient(180deg, #ffb909 1.6768%, #e17200 94.034%)';

const WeightProgressCard: React.FC<WeightProgressCardProps> = ({ progress, language, bare, weightUnit = 'kg' }) => {
	const t = (key: string) => translator.translate(language, 'my_account', `CareProfile/WeightProgress/${key}`);

	const content = (
		<>
			<div id="weight-progress-card-top" className="flex items-start justify-between gap-x-3">
				<div id="weight-progress-current" className="flex flex-col min-w-0">
					<p className="text-[14px] text-[#616975] leading-[21px]">{t('Current')}</p>
					<p id="weight-progress-current-value" className="text-[42px] leading-[52px] font-medium text-care-plus-heading tracking-[-1px]">
						{weightUnit === 'kg' ? (
							<>
								{progress.currentKg}
								<span className="text-[14px] font-medium text-[#616975] ml-1 align-baseline">kg</span>
							</>
						) : formatWeight(progress.currentKg, weightUnit)}
					</p>
				</div>

				<div className="flex flex-col items-end gap-y-2 pt-1 flex-shrink-0">
					{progress.totalLostKg > 0 && (
						<div
							id="weight-progress-lost-pill"
							className="flex items-center gap-x-2 rounded-full bg-[#faf4ef] border border-solid border-[rgba(255,185,9,0.2)] px-3 py-1.5 text-[#e88103] text-[12px] font-medium whitespace-nowrap"
						>
							<FiTrendingDown size={15} />
							<span>−{formatWeight(progress.totalLostKg, weightUnit)} {t('TotalLost')}</span>
						</div>
					)}
					{progress.goalKg !== null && (
						<p id="weight-progress-to-goal" className="text-[12px] uppercase tracking-[1px] text-[#616975] text-right">
							{formatWeight(progress.toGoalKg, weightUnit)} {t('ToGoal')}
						</p>
					)}
				</div>
			</div>

			<div id="weight-progress-bar-container" className="w-full h-[3px] bg-[#dee3ed] rounded-full overflow-hidden mt-[35px]">
				<div
					id="weight-progress-bar-fill"
					className="h-full rounded-full transition-all duration-300"
					style={{ width: `${progress.percent}%`, backgroundImage: progressGradient }}
				/>
			</div>

			<div id="weight-progress-stats-row" className="flex items-start justify-between mt-4">
				<div id="weight-progress-start" className="flex flex-col gap-y-0.5 items-start">
					<p className="text-[10px] uppercase tracking-[1px] text-[#99a3b2] leading-[12px]">{t('Start')}</p>
					<p className="text-[12px] font-medium text-care-plus-heading leading-[15px]">{formatWeight(progress.startKg, weightUnit)}</p>
				</div>
				<div id="weight-progress-percent" className="flex flex-col gap-y-0.5 items-center">
					<p className="text-[10px] uppercase tracking-[1px] text-[#99a3b2] leading-[12px]">{t('Progress')}</p>
					<p
						className="text-[12px] font-medium leading-[15px] bg-clip-text text-transparent"
						style={{ backgroundImage: progressGradient }}
					>
						{progress.percent > 0 ? `${progress.percent}%` : '--'}
					</p>
				</div>
				<div id="weight-progress-goal" className="flex flex-col gap-y-0.5 items-end">
					<p className="text-[10px] uppercase tracking-[1px] text-[#99a3b2] leading-[12px]">{t('Goal')}</p>
					<p className="text-[12px] font-medium text-care-plus-heading leading-[15px]">{progress.goalKg !== null ? formatWeight(progress.goalKg, weightUnit) : '--'}</p>
				</div>
			</div>
		</>
	);

	if (bare) {
		// Mobile-only variant. Uses a distinct id so the tour's desktop anchor (`weight-progress-card`)
		// never resolves to this hidden (`display:none`) element, which would report a zero rect.
		return <div id="weight-progress-card-mobile">{content}</div>;
	}

	return (
		<CareProfileCard id="weight-progress-card" className="flex flex-col overflow-hidden border border-solid border-[#e7e4db] rounded-[20px]" padding="md">
			{content}
		</CareProfileCard>
	);
};

export default WeightProgressCard;
